import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Facebook, Twitter, Instagram, Linkedin,
  Headphones, Phone, MessageCircle, Mail, Clock
} from 'lucide-react';
import api from '../../api/api';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState({ state: 'idle', message: '' });

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setSubscribeStatus({ state: 'error', message: 'Please enter a valid email.' });
      return;
    }
    setSubscribeStatus({ state: 'loading', message: '' });
    try {
      await api.post('/api/home/newsletter/', { email: email.trim() });
      setSubscribeStatus({ state: 'success', message: 'Subscribed!' });
      setEmail('');
      setTimeout(() => setSubscribeStatus({ state: 'idle', message: '' }), 4000);
    } catch (err) {
      setSubscribeStatus({ state: 'error', message: 'Failed to subscribe.' });
      setTimeout(() => setSubscribeStatus({ state: 'idle', message: '' }), 4000);
    }
  };

  return (
    <footer className="main-footer">
      <div className="footer-container">
        
        {/* Compact 5-Column Grid with all links and info preserved */}
        <div className="footer-compact-grid">
          
          {/* Column 1: Brand, Motto & Socials */}
          <div className="footer-col brand-col">
            <Link to="/" className="footer-logo-card" title="MusB Diagnostics">
              <img 
                src="/images/MusB_Diagnostic_Logo.webp" 
                alt="MusB Diagnostics Logo" 
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/images/MusB_Diagnostic_Logo.png';
                }}
              />
            </Link>
            <p className="footer-brand-motto">
              At Your Service for a Healthier Tomorrow.
            </p>
            <p className="footer-brand-desc">
              Pioneering next-generation diagnostics with research-grade accuracy and nationwide availability. Affordable, HIPAA-compliant laboratory solutions for the modern world.
            </p>
            
            {/* Social Icons */}
            <div className="footer-social-row">
              <a 
                href="https://www.linkedin.com/company/musb-diagnostic" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-social-btn" 
                aria-label="LinkedIn"
                title="LinkedIn"
              >
                <Linkedin size={14} />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61573528440734" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-social-btn" 
                aria-label="Facebook"
                title="Facebook"
              >
                <Facebook size={14} />
              </a>
              <a 
                href="https://www.instagram.com/musb.diagnostics/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-social-btn" 
                aria-label="Instagram"
                title="Instagram"
              >
                <Instagram size={14} />
              </a>
              <a 
                href="https://x.com/MusbDiagnostics" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-social-btn" 
                aria-label="Twitter / X"
                title="Twitter / X"
              >
                <Twitter size={14} />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="footer-col">
            <h4 className="footer-col-title">QUICK LINKS</h4>
            <ul className="footer-links-list">
              <li><Link to="/test-catalog">Test Catalog</Link></li>
              <li><Link to="/book">Book Appointment</Link></li>
              <li><Link to="/offers">Health Offers</Link></li>
              <li><Link to="/mobile-phlebotomy">Mobile Phlebotomy</Link></li>
              <li><Link to="/faq">FAQs</Link></li>
            </ul>
          </div>

          {/* Column 3: Legal & Safety */}
          <div className="footer-col">
            <h4 className="footer-col-title">LEGAL &amp; SAFETY</h4>
            <ul className="footer-links-list">
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/cookie-policy">Cookie Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/hipaa">HIPAA Compliance</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
              <li><Link to="/certifications">Certifications</Link></li>
            </ul>
          </div>

          {/* Column 4: Network Portals */}
          <div className="footer-col">
            <h4 className="footer-col-title">NETWORK PORTALS</h4>
            <ul className="footer-links-list">
              <li><Link to="/staff/login">Staff Login</Link></li>
              <li><Link to="/portal/login?portal=specialist">Specialist Portal</Link></li>
              <li><Link to="/portal/login?portal=hub">Hub Management</Link></li>
              <li><Link to="/portal/login?portal=research">Research Portal</Link></li>
              <li><Link to="/employer-health-program">Employer Program</Link></li>
              <li><Link to="/partnerships">Partner with Us</Link></li>
            </ul>
          </div>

          {/* Column 5: Stay Connected & Newsletter */}
          <div className="footer-col connected-col">
            <h4 className="footer-col-title">STAY CONNECTED</h4>
            
            {/* App Badges Row */}
            <div className="footer-store-badges">
              <a 
                href="https://apps.apple.com/in/app/musb-diagnostics/id6797962528" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-store-badge-btn appstore"
                title="Download on Apple App Store"
              >
                <svg viewBox="0 0 170 170" width="14" height="14" fill="currentColor">
                  <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.58-7.7-11.64-13.98-5.87-9.01-10.45-19.16-13.74-30.45-3.29-11.29-4.94-22.18-4.94-32.66 0-14.54 3.76-26.68 11.27-36.43 7.51-9.75 16.99-14.77 28.43-15.06 4.78 0 10.09 1.25 15.93 3.75 5.84 2.5 9.87 3.86 12.09 4.09 1.94-.34 6.13-1.84 12.57-4.51 6.44-2.67 12.09-3.77 16.94-3.32 12.4.92 22.36 5.58 29.89 13.98-10.74 6.5-16.02 15.48-15.84 26.94.19 8.94 3.65 16.43 10.38 22.47 6.73 6.04 14.88 9.54 24.45 10.51-2.45 7.42-5.46 14.78-9.03 22.08zM119.22 33.61c0-7.39 2.65-14.15 7.95-20.28 5.3-6.13 11.83-9.98 19.59-11.55.22 1.3.33 2.45.33 3.45 0 7.28-2.73 14.16-8.19 20.64-5.46 6.48-12.18 10.37-20.16 11.67-.32-1.3-.48-2.61-.48-3.93z"/>
                </svg>
                <div className="store-badge-texts">
                  <span className="store-sub">Download on the</span>
                  <span className="store-title">App Store</span>
                </div>
              </a>

              <a 
                href="https://play.google.com/store/apps/details?id=com.musb.diagnostics&hl=en_IN" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-store-badge-btn playstore"
                title="Download on Google Play"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a2.03 2.03 0 0 1-.22-.964V2.778c0-.36.08-.696.22-.964z" fill="#00D2FF" />
                  <path d="M17.436 8.356L14.77 11.02 3.609 1.814a2.07 2.07 0 0 1 1.62-.27l12.207 6.812z" fill="#00E676" />
                  <path d="M17.436 15.644L5.229 22.456c-.53.296-1.13.364-1.62.27l11.16-10.206 2.667 3.124z" fill="#FF3D00" />
                  <path d="M21.282 10.505l-3.846-2.149-2.666 2.664 2.666 2.664 3.846-2.148c.718-.401.718-1.63 0-2.031z" fill="#FFC107" />
                </svg>
                <div className="store-badge-texts">
                  <span className="store-sub">GET IT ON</span>
                  <span className="store-title">Google Play</span>
                </div>
              </a>
            </div>

            {/* Subscribe to Updates Form */}
            <div className="footer-sub-section">
              <h5 className="footer-sub-title">Subscribe to Updates</h5>
              <form className="footer-sub-form" onSubmit={handleSubscribe}>
                <div className="footer-sub-input-row">
                  <input 
                    type="email" 
                    placeholder="Your email address" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={subscribeStatus.state === 'loading'}
                    className="footer-sub-input"
                    aria-label="Your email address"
                    required
                  />
                  <button 
                    type="submit" 
                    className="footer-sub-btn"
                    disabled={subscribeStatus.state === 'loading'}
                  >
                    {subscribeStatus.state === 'loading' ? '...' : 'Subscribe'}
                  </button>
                </div>
                {subscribeStatus.message && (
                  <span className={`footer-sub-alert ${subscribeStatus.state}`}>
                    {subscribeStatus.message}
                  </span>
                )}
              </form>
            </div>
          </div>

          {/* Contact Support Card / Bar */}
          <div className="footer-contact-bar">
            <Link to="/contact" className="footer-contact-support-btn">
              <Headphones size={15} />
              <span>Contact Support</span>
            </Link>
            <a href="tel:+18134190781" className="footer-contact-info-pill">
              <Phone size={14} className="contact-icon-accent" />
              <span>+1 (813) 419-0781</span>
            </a>
            <a href="https://wa.me/17275050452" target="_blank" rel="noopener noreferrer" className="footer-contact-info-pill">
              <MessageCircle size={14} className="contact-icon-accent" />
              <span>WhatsApp +1 (727) 505-0452</span>
            </a>
            <a href="mailto:info@musbdiagnostics.com" className="footer-contact-info-pill">
              <Mail size={14} className="contact-icon-accent" />
              <span>info@musbdiagnostics.com</span>
            </a>
            <div className="footer-contact-info-pill hours-pill">
              <Clock size={14} className="contact-icon-accent" />
              <span>Mon-Fri 7AM-6PM | Sat 8AM-1PM EST</span>
            </div>
          </div>

        </div>

        {/* Bottom Bar: Copyright, CLIA & COLA Credentials, Motto */}
        <div className="footer-bottom-row">
          <p className="footer-bottom-copy">
            &copy; {currentYear} MusB Diagnostics. All Rights Reserved.
          </p>
          <div className="footer-bottom-compliance">
            <span>CLIA #10D2320720</span>
            <span className="compliance-dot">•</span>
            <span>COLA Accredited</span>
          </div>
          <p className="footer-bottom-motto">
            Healthier People. Stronger Communities.
          </p>
        </div>

      </div>
    </footer>
  );
};

export default Footer;

